// React imports
import React, { memo, ReactNode, useEffect } from 'react';

// Components imports
import { Box, Container } from '@mui/material';
import {
    CenterCircularProgress,
    FlexyTabs,
    ModificationDate,
    ModificationDateProps,
    PublisherHeader,
    TabStructure,
    Toolbar,
    ToolbarProps,
} from '@europrocurement/flexy-components';

import { useParams } from 'react-router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ActivityTab } from '@europrocurement/l2d-modules';

import { useCurrentPublisher, useSelectPublisher } from '../../../../../hooks/publisherHooks';

import PurchaseInvoiceSettings from './tabs/purchaseInvoiceSettings/PurchaseInvoiceSettings';
import Contacts from '../../../sections/Contacts';

export type ModalFunctions = {
    toggleModal: () => void;
    closeModal: () => void;
    openModal: () => void;
    setModalContent: (element: ReactNode | null) => void;
};

const PublisherDetail: React.FunctionComponent = function () {
    const { idPublisher: urlPublisherId } = useParams();

    const publisher = useCurrentPublisher();
    const selectPublisher = useSelectPublisher();

    const isLoading = !publisher;

    useEffect(() => {
        const { signal } = new AbortController();
        if (urlPublisherId) {
            selectPublisher(Number(urlPublisherId), {
                signal,
            });
        }
    }, [selectPublisher, urlPublisherId]);

    if (isLoading) {
        return <CenterCircularProgress sx={{ height: '70vh' }} />;
    }

    /** Toolbar ** actions ( waiting definition ) */
    const sendSomething = () => {};
    const editPrescriber = () => {};
    const toggleDropdown = () => {};

    const toolbarActions: ToolbarProps['actions'] = [
        {
            onClick: sendSomething,
            icon: ['fasl', 'paper-plane'],
        },
        {
            onClick: editPrescriber,
            icon: ['fasl', 'pen'],
        },
        {
            onClick: toggleDropdown,
            icon: ['fasl', 'ellipsis'],
        },
    ];

    const getModificationDateProps = (): ModificationDateProps => {
        const creationDate = publisher?.dateCreation || '';
        const updateDate = publisher?.dateMaj || '';
        const isCreationDate = !!(!updateDate && creationDate);

        return {
            date: new Date(isCreationDate ? creationDate : updateDate),
            isCreationDate,
        };
    };

    /** Toolbar / Breadcrumb ** LazyBreadcrumb path */
    const breadcrumbPath = [
        {
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: {
                    alignItems: 'center',
                    display: 'flex',
                },
            },
            path: 'Accueil',
            link: '/',
        },
        {
            path: 'Fournisseurs',
            link: '/fournisseurs/liste',
        },
        '',
    ];

    const hasTimestamp = publisher?.dateMAJ || publisher?.dateCreation;

    /** DEV: Used to filter availables / WIP tabs */
    const tabsShown = ['infos', 'config', 'history'];

    const tabProps = {
        publisher,
    };

    const tabs: Array<TabStructure> = [
        {
            tabName: 'infos',
            tabTitle: 'Informations générales',
            tabPanel: <Contacts {...tabProps} />,
        },
        {
            tabName: 'config',
            tabTitle: 'Configuration des relances',
            tabPanel: <PurchaseInvoiceSettings publisher={publisher} />,
        },
        {
            tabName: 'history',
            tabTitle: 'Activités',
            tabPanel: (
                <ActivityTab
                    showTechniqueLogs
                    showTask
                />
            ),
        },
    ];

    return (
        <Container maxWidth="lg">
            <Toolbar
                path={breadcrumbPath}
                actions={toolbarActions}
            >
                {hasTimestamp && (
                    <Box sx={{ opacity: 0.5 }}>
                        <ModificationDate {...getModificationDateProps()} />
                    </Box>
                )}
            </Toolbar>
            <PublisherHeader publisher={publisher} />
            <FlexyTabs
                routerParameterName="prescripteurTab"
                sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}
                tabs={tabs.filter((tab) => tabsShown.includes(tab.tabName))}
                scrollbar={false}
            />
        </Container>
    );
};

export default memo(PublisherDetail);
